import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";

import useApiHelper from "../hooks/useApiHelper";
import { useAuth } from "../contexts/AuthContext";
import { useSignalR } from "../contexts/SignalRContext";
import {
  IChatChannel,
  IChatCreateChannelBody,
  IChatMessage,
  IChatReadState,
  IChatSendMessageBody,
  IChatUser,
} from "../models/Chat";
import { DefaultResponse, DefaultResponseWithData } from "../models/system";

/** Messages fetched per page. The thread loads the newest page and the
 * user pulls older history in explicitly via a "load more" button, so a
 * busy channel never ships its whole backlog on open. */
export const CHAT_PAGE_SIZE = 50;

export const chatQueryKeys = {
  users: ["chat", "users"] as const,
  channels: ["chat", "channels"] as const,
  messages: (channelId: string) =>
    ["chat", "channels", channelId, "messages"] as const,
  readState: ["chat", "read-state"] as const,
  // Lightweight `{ total }` summary for the global sidebar badge. Kept
  // separate from `channels` so non-chat routes can render the badge
  // without having to load the full channel list.
  unreadSummary: ["chat", "unread-summary"] as const,
  // Per-channel digest of unread messages for the topbar notifications
  // drawer. Returns the latest unread message embedded so the drawer
  // can render previews without follow-up fetches.
  unreadChannels: ["chat", "unread-channels"] as const,
};

/** Per-channel unread digest row returned by GET /chat/unread-channels. */
export interface IChatUnreadChannel {
  channelId: string;
  channelKind: "channel" | "dm";
  channelName: string;
  memberIds: string[];
  unreadCount: number;
  latestMessageId?: string;
  latestAuthorId?: string;
  latestBody?: string;
  latestCreatedAt?: string;
}

export default function useChat() {
  const { get, post, postDocs, patch, del } = useApiHelper();
  const { loading: authLoading, loggedIn } = useAuth();
  const queryClient = useQueryClient();

  // Gate every chat query on the auth bootstrap completing - otherwise
  // the very first render fires `/chat/*` with a placeholder Bearer
  // header, the server returns 401, and React Query's default retry
  // backs off for ~10s before the page settles. Mutations don't need
  // the same guard - they're only invoked from user interactions which
  // happen after auth is ready.
  const authReady = !authLoading && loggedIn;

  const users = useQuery({
    queryKey: chatQueryKeys.users,
    queryFn: () => get<DefaultResponseWithData<IChatUser[]>>("/chat/users"),
    staleTime: 1000 * 30,
    refetchInterval: 1000 * 60,
    enabled: authReady,
  });

  const channels = useQuery({
    queryKey: chatQueryKeys.channels,
    queryFn: () => get<DefaultResponseWithData<IChatChannel[]>>("/chat/channels"),
    staleTime: 1000 * 30,
    enabled: authReady,
  });

  const readState = useQuery({
    queryKey: chatQueryKeys.readState,
    queryFn: () => get<DefaultResponseWithData<IChatReadState>>("/chat/read-state"),
    staleTime: 1000 * 30,
    enabled: authReady,
  });

  /** Per-channel message list, paginated. The query itself only ever holds
   * the *newest* page (so SignalR invalidations stay a single cheap fetch);
   * older pages the user has pulled in via `loadOlder` live in local state
   * and are merged in front of the live page at render time. Pass a null
   * channelId until a channel is selected so we don't fire requests for
   * every channel. */
  const useMessages = (channelId: string | null) => {
    const latestQuery = useQuery({
      queryKey: channelId
        ? chatQueryKeys.messages(channelId)
        : ["chat", "messages", "none"],
      queryFn: () =>
        get<DefaultResponseWithData<IChatMessage[]>>(
          `/chat/channels/${channelId}/messages?limit=${CHAT_PAGE_SIZE}`
        ),
      enabled: authReady && !!channelId,
      staleTime: 1000 * 15,
    });

    const [older, setOlder] = useState<IChatMessage[]>([]);
    const [exhausted, setExhausted] = useState(false);
    const [loadingOlder, setLoadingOlder] = useState(false);

    // Older history belongs to one channel only - drop it on switch.
    useEffect(() => {
      setOlder([]);
      setExhausted(false);
      setLoadingOlder(false);
    }, [channelId]);

    const latest = useMemo(
      () => latestQuery.data?.data ?? [],
      [latestQuery.data]
    );

    // Merge: older pages first, then the live page, deduped by id (a
    // message can appear in both when the live page refetches after new
    // messages arrive and slides its window backwards).
    const messages = useMemo(() => {
      if (older.length === 0) return latest;
      const liveIds = new Set(latest.map((m) => m.id));
      return [...older.filter((m) => !liveIds.has(m.id)), ...latest];
    }, [older, latest]);

    // A short first page means the channel has no more history. Once a
    // "load more" comes back short we latch `exhausted` instead.
    const hasMore =
      !exhausted && !latestQuery.isLoading && latest.length >= CHAT_PAGE_SIZE;

    const loadOlder = useCallback(async () => {
      if (!channelId || loadingOlder || exhausted) return;
      const oldest = messages[0];
      if (!oldest) return;

      setLoadingOlder(true);
      try {
        const res = await get<DefaultResponseWithData<IChatMessage[]>>(
          `/chat/channels/${channelId}/messages?limit=${CHAT_PAGE_SIZE}&before=${encodeURIComponent(
            oldest.createdAt
          )}`
        );
        const page = res?.data ?? [];
        if (page.length < CHAT_PAGE_SIZE) setExhausted(true);
        if (page.length > 0) {
          setOlder((current) => {
            const known = new Set(current.map((m) => m.id));
            return [...page.filter((m) => !known.has(m.id)), ...current];
          });
        }
      } finally {
        setLoadingOlder(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channelId, loadingOlder, exhausted, messages]);

    return {
      messages,
      isLoading: latestQuery.isLoading,
      hasMore,
      loadingOlder,
      loadOlder,
    };
  };

  const sendMessage = useMutation(
    ({
      channelId,
      body,
    }: {
      channelId: string;
      body: IChatSendMessageBody;
    }) =>
      post<IChatSendMessageBody, DefaultResponseWithData<IChatMessage>>(
        `/chat/channels/${channelId}/messages`,
        body
      ),
    {
      onSuccess: (res) => {
        const created = res?.data;
        if (created?.channelId) {
          queryClient.invalidateQueries(chatQueryKeys.messages(created.channelId));
          queryClient.invalidateQueries(chatQueryKeys.channels);
          queryClient.invalidateQueries(chatQueryKeys.unreadSummary);
        }
      },
    }
  );

  // Multipart sibling of sendMessage for messages carrying files. The
  // text body and replyToId ride along as form fields next to the files.
  const sendMessageWithAttachments = useMutation(
    ({
      channelId,
      body,
      files,
    }: {
      channelId: string;
      body: IChatSendMessageBody;
      files: File[];
    }) => {
      const formData = new FormData();
      formData.append("body", body.body ?? "");
      if (body.replyToId) formData.append("replyToId", body.replyToId);
      files.forEach((file) => formData.append("files", file, file.name));
      return postDocs<DefaultResponseWithData<IChatMessage>>(
        `/chat/channels/${channelId}/messages/attachments`,
        formData
      );
    },
    {
      onSuccess: (res) => {
        const created = res?.data;
        if (created?.channelId) {
          queryClient.invalidateQueries(chatQueryKeys.messages(created.channelId));
          queryClient.invalidateQueries(chatQueryKeys.channels);
          queryClient.invalidateQueries(chatQueryKeys.unreadSummary);
        }
      },
    }
  );

  const editMessage = useMutation(
    ({ messageId, body }: { messageId: string; body: string }) =>
      patch<DefaultResponseWithData<IChatMessage>>(
        `/chat/messages/${messageId}`,
        { body }
      ),
    {
      onSuccess: (res) => {
        const updated = res?.data;
        if (updated?.channelId) {
          queryClient.invalidateQueries(chatQueryKeys.messages(updated.channelId));
        }
      },
    }
  );

  const toggleReaction = useMutation(
    ({ messageId, emoji }: { messageId: string; emoji: string }) =>
      post<null, DefaultResponseWithData<IChatMessage>>(
        `/chat/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`,
        null
      ),
    {
      onSuccess: (res) => {
        const updated = res?.data;
        if (updated?.channelId) {
          queryClient.invalidateQueries(chatQueryKeys.messages(updated.channelId));
        }
      },
    }
  );

  const createChannel = useMutation(
    (body: IChatCreateChannelBody) =>
      post<IChatCreateChannelBody, DefaultResponseWithData<IChatChannel>>(
        "/chat/channels",
        body
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(chatQueryKeys.channels);
      },
    }
  );

  const startDm = useMutation(
    (otherUserId: string) =>
      post<{ userId: string }, DefaultResponseWithData<IChatChannel>>(
        "/chat/channels/dm",
        { userId: otherUserId }
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(chatQueryKeys.channels);
      },
    }
  );

  const markRead = useMutation(
    (channelId: string) =>
      post<null, DefaultResponse>(
        `/chat/channels/${channelId}/read`,
        null
      ),
    {
      //Optimistically zero this channel's unread count and shrink the
      //global badge total by the same amount so both the per-channel
      //badge and the sidebar nav badge clear the instant the user
      //opens the channel - no waiting for a server round-trip.
      onMutate: (channelId) => {
        let cleared = 0;
        queryClient.setQueryData<DefaultResponseWithData<IChatChannel[]>>(
          chatQueryKeys.channels,
          (old) => {
            if (!old?.data) return old;
            return {
              ...old,
              data: old.data.map((c) => {
                if (c.id !== channelId) return c;
                if (!c.muted) cleared = c.unreadCount ?? 0;
                return { ...c, unreadCount: 0 };
              }),
            };
          }
        );
        if (cleared > 0) {
          queryClient.setQueryData<DefaultResponseWithData<{ total: number }>>(
            chatQueryKeys.unreadSummary,
            (old) => {
              if (!old?.data) return old;
              return {
                ...old,
                data: { total: Math.max(0, (old.data.total ?? 0) - cleared) },
              };
            }
          );
        }
      },
      onSuccess: () => {
        queryClient.invalidateQueries(chatQueryKeys.readState);
        queryClient.invalidateQueries(chatQueryKeys.channels);
        queryClient.invalidateQueries(chatQueryKeys.unreadSummary);
        queryClient.invalidateQueries(chatQueryKeys.unreadChannels);
      },
    }
  );

  const addChannelMembers = useMutation(
    ({ channelId, userIds }: { channelId: string; userIds: string[] }) =>
      post<{ userIds: string[] }, DefaultResponseWithData<IChatChannel>>(
        `/chat/channels/${channelId}/members`,
        { userIds }
      ),
    {
      onSuccess: (res) => {
        const updated = res?.data;
        if (!updated) return;
        queryClient.setQueryData<DefaultResponseWithData<IChatChannel[]>>(
          chatQueryKeys.channels,
          (old) => {
            if (!old?.data) return old;
            return {
              ...old,
              data: old.data.map((c) => (c.id === updated.id ? updated : c)),
            };
          }
        );
      },
    }
  );

  const removeChannelMember = useMutation(
    ({ channelId, userId }: { channelId: string; userId: string }) =>
      del<DefaultResponseWithData<IChatChannel>>(
        `/chat/channels/${channelId}/members/${userId}`
      ),
    {
      onSuccess: (res) => {
        const updated = res?.data;
        if (!updated) return;
        queryClient.setQueryData<DefaultResponseWithData<IChatChannel[]>>(
          chatQueryKeys.channels,
          (old) => {
            if (!old?.data) return old;
            return {
              ...old,
              data: old.data.map((c) => (c.id === updated.id ? updated : c)),
            };
          }
        );
      },
    }
  );

  const setChannelMuted = useMutation(
    ({ channelId, muted }: { channelId: string; muted: boolean }) =>
      post<{ muted: boolean }, DefaultResponseWithData<IChatChannel>>(
        `/chat/channels/${channelId}/mute`,
        { muted }
      ),
    {
      //Patch the cached channel in place so the toggle feels instant.
      //Server confirmation arrives via the response and a SignalR
      //`ChannelUpdated` event broadcast to the caller's other sessions.
      //Also nudge the global unread summary - muting/unmuting a noisy
      //channel changes the badge total and we want that reflected
      //immediately rather than waiting for the next poll.
      onSuccess: (res) => {
        const updated = res?.data;
        if (!updated) return;
        queryClient.setQueryData<DefaultResponseWithData<IChatChannel[]>>(
          chatQueryKeys.channels,
          (old) => {
            if (!old?.data) return old;
            return {
              ...old,
              data: old.data.map((c) => (c.id === updated.id ? updated : c)),
            };
          }
        );
        queryClient.invalidateQueries(chatQueryKeys.unreadSummary);
        queryClient.invalidateQueries(chatQueryKeys.unreadChannels);
      },
    }
  );

  return {
    users,
    channels,
    readState,
    useMessages,
    sendMessage,
    sendMessageWithAttachments,
    editMessage,
    toggleReaction,
    createChannel,
    startDm,
    markRead,
    setChannelMuted,
    addChannelMembers,
    removeChannelMember,
  };
}

// ---------------------------------------------------------------------------
// In-channel message search. Standalone so the header search panel can use
// it without pulling in the full chat hook. The server requires a term of
// at least two characters; shorter terms simply keep the query disabled.
// ---------------------------------------------------------------------------
export function useChatMessageSearch(channelId: string | null, term: string) {
  const { get } = useApiHelper();
  const trimmed = term.trim();

  return useQuery({
    queryKey: ["chat", "channels", channelId ?? "none", "search", trimmed],
    queryFn: () =>
      get<DefaultResponseWithData<IChatMessage[]>>(
        `/chat/channels/${channelId}/messages/search?term=${encodeURIComponent(
          trimmed
        )}`
      ),
    enabled: !!channelId && trimmed.length >= 2,
    staleTime: 1000 * 30,
    keepPreviousData: true,
  });
}

// ---------------------------------------------------------------------------
// Member-guarded attachment download. Standalone (rather than part of
// useChat) so message rows can wire it up without instantiating the full
// chat hook's query subscriptions per row. Returns a Blob the caller can
// turn into an object URL (image preview) or a save-as download.
// ---------------------------------------------------------------------------
export function useChatAttachmentDownload() {
  const { getFile } = useApiHelper();

  return useMutation(
    ({
      channelId,
      attachmentId,
    }: {
      channelId: string;
      attachmentId: string;
    }) => getFile(`/chat/channels/${channelId}/attachments/${attachmentId}`)
  );
}

// ---------------------------------------------------------------------------
// Lightweight hook for places that only need the global unread badge total
// (sidebar nav, topbar, etc). Hits the dedicated `/chat/unread-summary`
// endpoint which is a single COUNT(*) on the server, so the badge can
// render quickly on a cold dashboard load without having to wait for the
// full channel list to materialise. Also subscribes to SignalR events so
// the badge stays live when the user is on a non-chat route.
//
// If the chat page is already mounted and has a populated channel list in
// the cache, we use that as a synchronous initial value so the badge
// number doesn't briefly flash to 0 while the summary request is in
// flight.
// ---------------------------------------------------------------------------
export function useChatUnreadTotal(): number {
  const { get } = useApiHelper();
  const { loading: authLoading, loggedIn } = useAuth();
  const queryClient = useQueryClient();
  const { chatConnection } = useSignalR();

  const summary = useQuery({
    queryKey: chatQueryKeys.unreadSummary,
    queryFn: () =>
      get<DefaultResponseWithData<{ total: number }>>("/chat/unread-summary"),
    staleTime: 1000 * 30,
    //60s poll covers any case where SignalR drops a message - a one-off
    //extra request per minute is well within budget for the value of a
    //live badge.
    refetchInterval: 1000 * 60,
    enabled: !authLoading && loggedIn,
    //Seed from the channels cache if the chat page has already loaded
    //it, so the badge number never blinks back to zero on remount.
    initialData: () => {
      const cached = queryClient.getQueryData<
        DefaultResponseWithData<IChatChannel[]>
      >(chatQueryKeys.channels);
      const list = cached?.data;
      if (!list) return undefined;
      const total = list
        .filter((c) => !c.muted)
        .reduce((sum, c) => sum + (c.unreadCount ?? 0), 0);
      return {
        success: true,
        message: "",
        data: { total },
      } as DefaultResponseWithData<{ total: number }>;
    },
  });

  useEffect(() => {
    if (!chatConnection) return;
    const refresh = () => {
      queryClient.invalidateQueries(chatQueryKeys.unreadSummary);
      queryClient.invalidateQueries(chatQueryKeys.channels);
    };
    chatConnection.on("MessageReceived", refresh);
    chatConnection.on("ChannelUpdated", refresh);
    chatConnection.on("ChannelCreated", refresh);
    chatConnection.on("ChannelRemoved", refresh);
    return () => {
      chatConnection.off("MessageReceived", refresh);
      chatConnection.off("ChannelUpdated", refresh);
      chatConnection.off("ChannelCreated", refresh);
      chatConnection.off("ChannelRemoved", refresh);
    };
  }, [chatConnection, queryClient]);

  return summary.data?.data?.total ?? 0;
}

// ---------------------------------------------------------------------------
// Per-channel unread digest for the topbar notifications drawer. Returns the
// list of channels the user has unread messages in, with the latest unread
// message preview attached. Live-refreshed off SignalR events so a new
// message immediately surfaces a row even on non-chat routes.
// ---------------------------------------------------------------------------
export function useChatNotifications() {
  const { get } = useApiHelper();
  const { loading: authLoading, loggedIn } = useAuth();
  const queryClient = useQueryClient();
  const { chatConnection } = useSignalR();

  const query = useQuery({
    queryKey: chatQueryKeys.unreadChannels,
    queryFn: () =>
      get<DefaultResponseWithData<IChatUnreadChannel[]>>(
        "/chat/unread-channels"
      ),
    staleTime: 1000 * 30,
    refetchInterval: 1000 * 60,
    enabled: !authLoading && loggedIn,
  });

  useEffect(() => {
    if (!chatConnection) return;
    const refresh = () => {
      queryClient.invalidateQueries(chatQueryKeys.unreadChannels);
    };
    chatConnection.on("MessageReceived", refresh);
    chatConnection.on("MessageEdited", refresh);
    chatConnection.on("ChannelUpdated", refresh);
    chatConnection.on("ChannelCreated", refresh);
    chatConnection.on("ChannelRemoved", refresh);
    return () => {
      chatConnection.off("MessageReceived", refresh);
      chatConnection.off("MessageEdited", refresh);
      chatConnection.off("ChannelUpdated", refresh);
      chatConnection.off("ChannelCreated", refresh);
      chatConnection.off("ChannelRemoved", refresh);
    };
  }, [chatConnection, queryClient]);

  return {
    rows: query.data?.data ?? [],
    isLoading: query.isLoading,
  };
}

